import React from "react";
import {
  ClickAwayListener,
  Collapse,
  IconButton,
  TextField,
  Typography,
  Link,
  Tooltip,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Box } from "@mui/system";
import ReactMarkdown from "react-markdown";
import { useMediaQuery } from "react-responsive";
import { getColorWithMode } from "../constants/colors";
import { updatePack } from "../utils";
import gfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import DOMPurify from "dompurify";

const COLLAPSED_HEIGHT = 56;
const PEEK_HEIGHT = 104;

const PackDescription = ({
  packDescription,
  setPackDescription,
  tableId,
  shareable,
  darkMode,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const [expanded, setExpanded] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [needsCollapse, setNeedsCollapse] = React.useState(false);
  const [contentHeight, setContentHeight] = React.useState(0);
  const contentRef = React.useRef(null);
  const [temporaryDescription, setTemporaryDescription] = React.useState(
    packDescription || ""
  );

  const handleUpdatePackDescription = () => {
    const newData = { packDescription: temporaryDescription || "" };
    setPackDescription(temporaryDescription);
    updatePack(tableId, newData);
  };

  React.useEffect(() => {
    setTemporaryDescription(packDescription || "");
  }, [packDescription]);

  // Layout effect so a long description mounts already collapsed instead of
  // flashing open and animating shut.
  React.useLayoutEffect(() => {
    const el = contentRef.current;
    const height = el ? el.scrollHeight : 0;
    setContentHeight(height);
    setNeedsCollapse(height > COLLAPSED_HEIGHT + 8);
  }, [temporaryDescription, shareable]);

  const isOpen = expanded || !needsCollapse;

  const renderContent = () => {
    if (!shareable) {
      return (
        <TextField
          multiline
          variant="standard"
          value={temporaryDescription}
          sx={{
            width: "100%",
            padding: "5px 5px 5px 10px",
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: getColorWithMode(darkMode, "text"),
            },
          }}
          InputProps={{
            disableUnderline: true,
            readOnly: shareable,
            sx: {
              fontSize: isMobile ? "24px" : "16px",
            },
          }}
          inputProps={{
            maxLength: 50000,
          }}
          placeholder="Description"
          onFocus={() => {
            setFocused(true);
            setExpanded(true);
          }}
          onChange={(e) => {
            setTemporaryDescription(e.target.value);
          }}
          onBlur={() => {
            setFocused(false);
            handleUpdatePackDescription();
          }}
        />
      );
    }

    // Sanitize HTML within the content while preserving Markdown
    const sanitizedContent = DOMPurify.sanitize(temporaryDescription, {
      ALLOWED_TAGS: [
        "a",
        "img",
        "p",
        "br",
        "b",
        "i",
        "em",
        "strong",
        "blockquote",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "width", "height"],
      ALLOW_DATA_ATTR: false,
      ADD_ATTR: ["target"],
      FORBID_TAGS: ["style", "script", "iframe", "form", "input", "button"],
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "style"],
    });

    return (
      <Box
        sx={{
          "& ul": {
            listStyleType: "disc",
            paddingLeft: "2em",
            marginBottom: "1em",
          },
          "& ol": {
            listStyleType: "decimal",
            paddingLeft: "2em",
            marginBottom: "1em",
          },
          "& li": {
            marginBottom: "0.5em",
          },
          "& p": {
            marginBottom: "1em",
          },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            marginTop: "1em",
            marginBottom: "0.5em",
            fontWeight: "bold",
          },
          "& blockquote": {
            borderLeft: `4px solid ${darkMode ? "#404040" : "#e0e0e0"}`,
            margin: "1em 0",
            padding: "0.5em 1em",
            backgroundColor: darkMode ? "#333333" : "#f5f5f5",
            borderRadius: "0 4px 4px 0",
            "& p": {
              margin: 0,
              color: darkMode ? "#e0e0e0" : "#666666",
            },
          },
          "& blockquote p": {
            marginBottom: 0,
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
            borderRadius: "8px",
            margin: "16px 0",
          },
          "& a": {
            color: darkMode ? "#90caf9" : "#1976d2",
            textDecoration: "none",
            "&:hover": {
              color: darkMode ? "#42a5f5" : "#1565c0",
              textDecoration: "underline",
            },
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[[gfm, { singleTilde: false }]]}
          rehypePlugins={[rehypeRaw]}
          components={{
            a: ({ node, children, href, ...props }) => (
              <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: darkMode ? "#90caf9" : "#1976d2",
                  "&:hover": {
                    color: darkMode ? "#42a5f5" : "#1565c0",
                  },
                }}
                {...props}
              >
                {children}
              </Link>
            ),
            img: ({ node, src, alt, ...props }) => (
              <Box
                component="img"
                src={src}
                alt={alt || "Image"}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  margin: "16px 0",
                }}
                {...props}
              />
            ),
            blockquote: ({ node, children, ...props }) => (
              <Box
                component="blockquote"
                sx={{
                  borderLeft: `4px solid ${darkMode ? "#404040" : "#e0e0e0"}`,
                  margin: "1em 0",
                  padding: "0.5em 1em",
                  backgroundColor: darkMode ? "#333333" : "#f5f5f5",
                  borderRadius: "0 4px 4px 0",
                  "& p": {
                    margin: 0,
                    color: darkMode ? "#e0e0e0" : "#666666",
                  },
                }}
                {...props}
              >
                {children}
              </Box>
            ),
          }}
        >
          {sanitizedContent}
        </ReactMarkdown>
      </Box>
    );
  };

  if (shareable && !temporaryDescription.length) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={() => setExpanded(false)}>
      <Box
        sx={{
          marginTop: "-5px",
          marginBottom: !shareable && focused ? "-15px" : "0px",
        }}
      >
        <Box
          sx={{ position: "relative", cursor: isOpen ? "default" : "pointer" }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => {
            if (!isOpen) setExpanded(true);
          }}
        >
          <Collapse
            in={isOpen}
            collapsedSize={
              !isOpen && hovered
                ? Math.min(PEEK_HEIGHT, contentHeight || PEEK_HEIGHT)
                : COLLAPSED_HEIGHT
            }
            sx={{
              transition:
                "height 300ms cubic-bezier(0.4, 0, 0.2, 1), min-height 250ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Box
              ref={contentRef}
              sx={{
                borderRadius: "10px",
                backgroundColor: darkMode ? "#2A2A2A" : "#F0F0F0",
                padding: "16px",
              }}
            >
              {renderContent()}
            </Box>
          </Collapse>
          {!isOpen && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "30px",
                borderRadius: "0 0 10px 10px",
                pointerEvents: "none",
                background: darkMode
                  ? "linear-gradient(rgba(42, 42, 42, 0), #2A2A2A)"
                  : "linear-gradient(rgba(240, 240, 240, 0), #F0F0F0)",
              }}
            />
          )}
          {needsCollapse && (
            <Tooltip
              title={isOpen ? "Collapse description" : "Expand description"}
            >
              <IconButton
                size="small"
                aria-label={
                  isOpen ? "Collapse description" : "Expand description"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!isOpen);
                }}
                sx={{
                  position: "absolute",
                  top: "6px",
                  right: "6px",
                  color: darkMode
                    ? "rgba(255, 255, 255, 0.4)"
                    : "rgba(0, 0, 0, 0.35)",
                }}
              >
                {isOpen ? (
                  <ChevronUp size={isMobile ? 20 : 16} />
                ) : (
                  <ChevronDown size={isMobile ? 20 : 16} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {!shareable && focused && (
          <Typography variant="subtitle2" sx={{ opacity: "0.8" }}>
            We support both Markdown and{" "}
            <Tooltip
              title="We support a, img, p, br, b, i,
            em and strong tags"
            >
              basic HTML tags
            </Tooltip>
          </Typography>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default PackDescription;
